import { useQueryClient } from '@tanstack/react-query'
import type { GridApi } from 'ag-grid-community'
import { message } from 'antd'

import type { InvoiceDetail, TransactionDetail } from '../../../../../api/useFreight/types'
import { useTransactions } from '../../../../../api/useFreight/useTransactions'

/**
 * Props for the useLineItemManagement hook
 */
interface UseLineItemManagementProps {
  api: GridApi
  data: InvoiceDetail | TransactionDetail
}

/**
 * Return type for the useLineItemManagement hook
 */
interface UseLineItemManagementReturn {
  handleAddLineItem: (
    orderId: string,
    accessorialId: string,
    amount: number,
    onComplete?: () => void,
    customerId?: string
  ) => Promise<void>
  handleApprove: (lineItemId: string, bookType: 'Cost' | 'Revenue', customerId?: string | null) => Promise<void>
  handleReject: (lineItemId: string, bookType: 'Cost' | 'Revenue') => Promise<void>
}

/**
 * Hook for managing line item operations such as adding, approving, and rejecting
 *
 * @param props - Props containing the grid API and data
 * @returns Functions for managing line items
 */
export const useLineItemManagement = ({ api }: UseLineItemManagementProps): UseLineItemManagementReturn => {
  const { useAddManualAccessorialMutation, useApproveTransactionMutation, useRejectTransactionMutation } =
    useTransactions()
  const queryClient = useQueryClient()

  const invalidateRelevantQueries = () => {
    // List of all query keys that need to be invalidated when line items change
    const queryKeys = [
      ['invoice'],
      ['invoices-be-query'],
      ['allTransactionsQuery'],
      ['estimatedTransactions'],
      ['openTransactions'],
      ['postedTransactions'],
      ['transactionsQuery'],
      ['transactionByOrder'],
    ]

    // Invalidate each query
    for (const key of queryKeys) {
      queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const refreshGrid = () => {
    // Only refresh if API is available
    if (api) {
      setTimeout(() => {
        api.refreshCells({ force: true })
      }, 1500)
    }
  }

  /**
   * Add a manual accessorial line item
   */
  const handleAddLineItem = async (
    orderId: string,
    accessorialId: string,
    amount: number,
    onComplete?: () => void,
    customerId?: string
  ): Promise<void> => {
    await useAddManualAccessorialMutation.mutateAsync({
      order_id: orderId,
      accessorial_id: accessorialId,
      amount,
      book_type: 'Revenue',
      customer_id: customerId,
    })

    invalidateRelevantQueries()

    if (onComplete) {
      onComplete()
    }
  }

  /**
   * Approve a transaction line item
   */
  const handleApprove = async (
    lineItemId: string,
    bookType: 'Cost' | 'Revenue',
    customerId?: string | null
  ): Promise<void> => {
    try {
      await useApproveTransactionMutation.mutateAsync({
        transaction_id: lineItemId,
        book_type: bookType,
        reason_code: 'Customer Request',
        customer_id: customerId || undefined,
      })

      invalidateRelevantQueries()
      message.success('Line item approved successfully')
      refreshGrid()
    } catch (error) {
      console.error('Error approving line item:', error)
      message.error(error instanceof Error ? error.message : 'Failed to approve line item')
    }
  }

  /**
   * Reject a transaction line item
   */
  const handleReject = async (lineItemId: string, bookType: 'Cost' | 'Revenue'): Promise<void> => {
    try {
      await useRejectTransactionMutation.mutateAsync({
        transaction_id: lineItemId,
        book_type: bookType,
      })

      message.success('Line item rejected successfully')
      invalidateRelevantQueries()
      refreshGrid()
    } catch (error) {
      console.error('Error rejecting line item:', error)
      message.error(error instanceof Error ? error.message : 'Failed to reject line item')
    }
  }

  return {
    handleAddLineItem,
    handleApprove,
    handleReject,
  }
}
